import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { Artwork } from '@/lib/artwork'
import { requiredKeys, specAddonTotal } from '@/lib/options'
import { urgentRateFor } from '@/lib/urgent'
import type { ItemSpec, OptionAddons, OptionGroup, PersonalizationRow, StorefrontProduct, StorefrontSizeGroup, TierPriceRow, UrgentTier } from '@/types'

const STORAGE_KEY = 'mpg-cart'

/**
 * A configured product waiting for checkout. Carries a product snapshot
 * (including the full tier price table) so guests can build a cart before
 * signing in - the server re-prices at the customer's real tier on checkout.
 */
export interface CartItem {
  uid: string
  productId: number
  name: string
  category: string
  image_url?: string | null
  image_color?: string | null
  sizes: string[]
  tier_prices: TierPriceRow[]
  /**
   * The price agreed with this customer for this product, which beats every
   * tier row above. Null means they have none; absent means the cart was
   * saved before agreed prices existed and the tier price still applies,
   * which is the safe reading either way.
   */
  special_price?: number | null
  /**
   * Sizes priced apart (Kids 22-32), each with its tier ladder and this
   * customer's own price. Absent on carts saved before groups existed, read
   * as none - the server re-prices anyway.
   */
  size_price_groups?: StorefrontSizeGroup[]
  /** Per-option surcharge snapshot; absent on carts saved before add-ons existed. */
  option_addons?: OptionAddons
  /** RM per piece when the item is personalized; absent = free or an older cart. */
  personalization_price?: number
  /**
   * The rush charge by quantity. Empty means this product cannot be rushed;
   * absent means the same, and refusing the rush is the safe reading. Carts
   * saved with the old flat urgent_surcharge are converted in load().
   */
  urgent_tiers?: UrgentTier[]
  /**
   * Option group keys the product insisted on when the line was added
   * (requiredKeys). Absent on carts saved before compulsory options existed,
   * which read as nothing to check - the server still refuses at checkout.
   */
  required_options?: string[]
  spec: ItemSpec
  sizeMatrix: Record<string, number>
  personalizations: PersonalizationRow[]
  artworkName: string | null
  /**
   * The uploaded file's server reference, when the customer was signed in on
   * the product page and the upload happened there. artworkName alone means
   * only the filename was captured and checkout still has to collect the file.
   */
  artwork?: Artwork | null
  /**
   * Every uploaded file in order (front, back, ...), from Buy again or a
   * checkout in progress. Read through cartArtworks(), which also takes the
   * single `artwork` a cart saved before several files existed carries.
   */
  artworks?: Artwork[]
  /**
   * The font files behind the names and numbers (client request 2026-10-14),
   * uploaded on the product page or brought by Buy again. Checkout requires
   * one on a line with names. Absent on older carts, read as none.
   */
  fonts?: Artwork[]
  /**
   * Whether this line is in the order being checked out. Everything is ticked
   * by default, so a customer who never touches a tick box sees the cart
   * behave exactly as it did before selection existed. Optional because a cart
   * saved before the field existed must still parse, and absent reads as
   * ticked - the opposite polarity to urgent_tiers, because here the safe
   * reading is on: a returning customer must not find an empty checkout.
   */
  selected?: boolean
}

interface CartValue {
  items: CartItem[]
  /** The ticked lines - what checkout prices, quotes, validates and orders. */
  selectedItems: CartItem[]
  add: (item: Omit<CartItem, 'uid'>) => void
  /**
   * Append several lines at once. With selectOnly, the new lines arrive ticked
   * and every line already in the cart is unticked, in one state update - what
   * Buy again needs, so the cart never flashes through a state where the old
   * lines are ticked too.
   */
  addMany: (items: Omit<CartItem, 'uid'>[], opts?: { selectOnly?: boolean }) => void
  update: (uid: string, patch: Partial<CartItem>) => void
  remove: (uid: string) => void
  removeMany: (uids: string[]) => void
  toggle: (uid: string) => void
  selectAll: (on: boolean) => void
  clear: () => void
  /** Every line in the cart. The header badge counts this, not the selection. */
  totalPieces: number
  /** Only the ticked lines. Every checkout figure reads this. */
  selectedPieces: number
}

const CartContext = createContext<CartValue | null>(null)

export function pieces(item: CartItem): number {
  return Object.values(item.sizeMatrix).reduce((a, b) => a + b, 0)
}

/**
 * Flat per-piece price at the given tier (base tier as fallback), or the
 * price agreed with this customer when they have one, plus any per-option
 * add-ons, plus the name & number charge when the item is personalized (rows
 * present = every piece personalized). `groups` is the Product Options list
 * (useOptionGroups) the add-ons are read through, the same visible groups the
 * server prices with.
 *
 * An agreed price replaces the base term only - the add-ons and the rush
 * charge still stack on top, exactly as they do on the server.
 */
export function unitPrice(item: CartItem, tierName: string, groups: OptionGroup[] | undefined, urgentRate = 0): number {
  const row = item.tier_prices.find((t) => t.tier === tierName) ?? item.tier_prices[0]
  return (item.special_price ?? row?.price ?? 0) + addonsPerPiece(item, groups, urgentRate)
}

/** Option, name and rush add-ons per piece, the same on every size. */
function addonsPerPiece(item: CartItem, groups: OptionGroup[] | undefined, urgentRate: number): number {
  return (
    specAddonTotal(item.option_addons, item.spec, groups) +
    (item.personalizations.length > 0 ? (item.personalization_price ?? 0) : 0) +
    urgentRate
  )
}

const round2 = (n: number) => Math.round(n * 100) / 100

/** The size price group holding this size, if any. */
export function sizeGroupOf(groups: StorefrontSizeGroup[] | undefined, size: string): StorefrontSizeGroup | undefined {
  return groups?.find((group) => group.sizes.includes(size))
}

/**
 * A size group's base price per piece: the customer's own, else their tier's
 * row (tier name, as unitPrice reads it), else the entry tier's.
 */
export function groupBasePrice(group: StorefrontSizeGroup, tierName: string): number {
  const row = group.tier_prices.find((t) => t.tier === tierName) ?? group.tier_prices[0]
  return group.special_price ?? row?.price ?? 0
}

/** A group's sizes for a label: "22-32" for a run of three or more, else "22, 24". */
export function groupSizesLabel(sizes: string[]): string {
  return sizes.length > 2 ? `${sizes[0]}-${sizes[sizes.length - 1]}` : sizes.join(', ')
}

/** One price on a line: the sizes it covers, their pieces and the all-in per-piece price. */
export interface LinePriceRow {
  group: string | null
  sizes: Record<string, number>
  qty: number
  unit: number
}

/**
 * The line split by size price group, in size order, the way
 * OrderService::place prices it: each size's base (its group's price, else
 * unitPrice's base) plus the add-ons every size shares, rounded per piece.
 * One row for a line with no groups.
 */
export function linePriceRows(item: CartItem, tierName: string, groups: OptionGroup[] | undefined, urgentRate = 0): LinePriceRow[] {
  const rows = new Map<string, LinePriceRow>()
  const plain = unitPrice(item, tierName, groups, urgentRate)
  const addons = addonsPerPiece(item, groups, urgentRate)

  for (const [size, qty] of Object.entries(item.sizeMatrix)) {
    if (qty <= 0) continue
    const group = sizeGroupOf(item.size_price_groups, size)
    const key = group?.name ?? ''
    const row = rows.get(key) ?? {
      group: group?.name ?? null,
      sizes: {},
      qty: 0,
      unit: round2(group ? groupBasePrice(group, tierName) + addons : plain),
    }
    row.sizes[size] = qty
    row.qty += qty
    rows.set(key, row)
  }

  return [...rows.values()]
}

/** What the line costs: each price row's pieces times its price, summed. */
export function lineSubtotal(item: CartItem, tierName: string, groups: OptionGroup[] | undefined, urgentRate = 0): number {
  return round2(linePriceRows(item, tierName, groups, urgentRate).reduce((sum, row) => sum + round2(row.qty * row.unit), 0))
}

/** Pieces of this line's product across every line in `items`. */
export function productPieces(item: CartItem, items: CartItem[]): number {
  return items.filter((other) => other.productId === item.productId).reduce((sum, other) => sum + pieces(other), 0)
}

/**
 * The per-piece rush rate this line pays when `items` is ordered urgent, or
 * null when it cannot be rushed. The range is picked by the product's pieces
 * across all of `items`, as the server does, so two designs of 30 pcs price
 * as 60.
 */
export function urgentRate(item: CartItem, items: CartItem[]): number | null {
  return urgentRateFor(item.urgent_tiers, productPieces(item, items))
}

/** Whether every line in the cart may be produced as a rush order. */
export function canRush(items: CartItem[]): boolean {
  return items.length > 0 && items.every((item) => urgentRate(item, items) != null)
}

/** What the rush would add to the order, in RM, across every line. */
export function urgentTotal(items: CartItem[]): number {
  return items.reduce(
    (sum, item) => sum + pieces(item) * (urgentRate(item, items) ?? 0),
    0,
  )
}

/** A cart line's uploaded files, whichever field an older saved cart used. */
export function cartArtworks(item: Pick<CartItem, 'artwork' | 'artworks'>): Artwork[] {
  return item.artworks ?? (item.artwork ? [item.artwork] : [])
}

export function cartItemFrom(
  product: StorefrontProduct,
  draft: Pick<CartItem, 'spec' | 'sizeMatrix' | 'personalizations' | 'artworkName' | 'artwork' | 'artworks' | 'fonts'>,
): Omit<CartItem, 'uid'> {
  return {
    productId: product.id,
    name: product.name,
    category: product.category,
    image_url: product.image_url,
    image_color: product.image_color,
    sizes: product.sizes,
    tier_prices: product.tier_prices ?? [],
    special_price: product.special_price ?? null,
    size_price_groups: product.size_price_groups ?? [],
    option_addons: product.options?.option_addons,
    personalization_price: product.options?.personalization_price,
    urgent_tiers: product.urgent_tiers ?? [],
    required_options: requiredKeys(product.options),
    ...draft,
  }
}

function load(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    if (!Array.isArray(parsed)) return []

    // Absent reads as ticked, so a cart saved before selection existed still
    // checks out instead of looking empty. A cart saved with the old flat
    // rush fee keeps it as one open range; the server re-prices anyway.
    return parsed.map((item: CartItem & { urgent_surcharge?: number | null }) => {
      const { urgent_surcharge, ...rest } = item
      return {
        ...rest,
        urgent_tiers:
          item.urgent_tiers ?? (urgent_surcharge != null ? [{ min_qty: 1, max_qty: null, price: urgent_surcharge }] : []),
        selected: item.selected !== false,
      }
    })
  } catch {
    return []
  }
}

let seq = 0

/** Unique per line even for several added in the same millisecond. */
function newUid(): string {
  seq += 1
  return `${Date.now()}-${seq}-${Math.random().toString(36).slice(2, 8)}`
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(load)

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  }, [items])

  const add = useCallback((item: Omit<CartItem, 'uid'>) => {
    setItems((list) => [...list, { ...item, uid: newUid(), selected: true }])
  }, [])

  const addMany = useCallback((incoming: Omit<CartItem, 'uid'>[], opts?: { selectOnly?: boolean }) => {
    if (incoming.length === 0) return

    setItems((list) => [
      ...(opts?.selectOnly ? list.map((item) => ({ ...item, selected: false })) : list),
      ...incoming.map((item) => ({ ...item, uid: newUid(), selected: true })),
    ])
  }, [])

  const update = useCallback((uid: string, patch: Partial<CartItem>) => {
    setItems((list) => list.map((item) => (item.uid === uid ? { ...item, ...patch } : item)))
  }, [])

  const remove = useCallback((uid: string) => {
    setItems((list) => list.filter((item) => item.uid !== uid))
  }, [])

  const removeMany = useCallback((uids: string[]) => {
    const gone = new Set(uids)
    setItems((list) => list.filter((item) => !gone.has(item.uid)))
  }, [])

  const toggle = useCallback((uid: string) => {
    setItems((list) => list.map((item) => (item.uid === uid ? { ...item, selected: !item.selected } : item)))
  }, [])

  const selectAll = useCallback((on: boolean) => {
    setItems((list) => list.map((item) => ({ ...item, selected: on })))
  }, [])

  const clear = useCallback(() => setItems([]), [])

  const selectedItems = useMemo(() => items.filter((item) => item.selected), [items])
  const totalPieces = useMemo(() => items.reduce((sum, item) => sum + pieces(item), 0), [items])
  const selectedPieces = useMemo(
    () => selectedItems.reduce((sum, item) => sum + pieces(item), 0),
    [selectedItems],
  )

  const value = useMemo(
    () => ({
      items,
      selectedItems,
      add,
      addMany,
      update,
      remove,
      removeMany,
      toggle,
      selectAll,
      clear,
      totalPieces,
      selectedPieces,
    }),
    [
      items,
      selectedItems,
      add,
      addMany,
      update,
      remove,
      removeMany,
      toggle,
      selectAll,
      clear,
      totalPieces,
      selectedPieces,
    ],
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart(): CartValue {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within CartProvider')
  return ctx
}
